import { useCallback, useEffect, useState } from 'react';
import * as sched from 'sched';
import { useAuth } from './useAuth';

// ---------------------------------------------------------------------------
// Cross-consumer sync
// ---------------------------------------------------------------------------
//
// A detail page may toggle "Watch" while the dashboard panel is mounted in a
// sibling tab. Each consumer owns its own state but shares a tiny event bus
// so mutations propagate immediately without round-tripping through the
// network. Events carry the workspace_id they affect.

type Listener = (workspaceId: string) => void;
const listeners = new Set<Listener>();

function broadcast(workspaceId: string) {
  listeners.forEach((l) => l(workspaceId));
}

// ---------------------------------------------------------------------------
// HTTP helper
// ---------------------------------------------------------------------------

async function apiFetch<T>(
  url: string,
  token: string,
  options?: RequestInit,
): Promise<T> {
  const resp = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers ?? {}),
    },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`API ${resp.status}: ${text}`);
  }
  if (resp.status === 204 || resp.status === 202) {
    const text = await resp.text().catch(() => '');
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }
  return resp.json();
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function targetsEqual(a: sched.WatchTarget, b: sched.WatchTarget): boolean {
  return a.type === b.type && a.id === b.id;
}

export function useWatchlist(workspaceId: string | undefined) {
  const { token } = useAuth();
  const apiUrl = process.env.API_URL ?? '';

  const [watches, setWatches] = useState<sched.Watch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!token || !workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<sched.ListWatchesResponse>(
        `${apiUrl}/api/workspaces/${workspaceId}/viewer/watches`,
        token,
      );
      setWatches(data.watches);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, workspaceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Subscribe to mutations from sibling consumers in other components.
  useEffect(() => {
    if (!workspaceId) return;
    const listener: Listener = (ws) => {
      if (ws === workspaceId) refresh();
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [workspaceId, refresh]);

  const addWatch = useCallback(
    async (target: sched.WatchTarget) => {
      if (!token || !workspaceId) return;
      const body: sched.CreateWatchRequest = { target, config: null };
      await apiFetch<sched.Watch>(
        `${apiUrl}/api/workspaces/${workspaceId}/viewer/watches`,
        token,
        { method: 'POST', body: JSON.stringify(body) },
      );
      broadcast(workspaceId);
    },
    [apiUrl, token, workspaceId],
  );

  const removeWatch = useCallback(
    async (target: sched.WatchTarget) => {
      if (!token || !workspaceId) return;
      await apiFetch<void>(
        `${apiUrl}/api/workspaces/${workspaceId}/viewer/watches/${target.type}/${target.id}`,
        token,
        { method: 'DELETE' },
      );
      broadcast(workspaceId);
    },
    [apiUrl, token, workspaceId],
  );

  const isWatching = useCallback(
    (target: sched.WatchTarget) => watches.some((w) => targetsEqual(w.target, target)),
    [watches],
  );

  return { watches, loading, error, refresh, addWatch, removeWatch, isWatching };
}
